'use client';
import {
  calculateBoundsForRadius,
  calculateInsideBoundingBox,
  ESearchRadiusLevels,
} from '@/app/lib/store-locator-utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ConfigureProps } from 'react-instantsearch';
import { SYD_TOWN_HALL } from '@workspace/constants';
import { AdvancedMarker, useMap, useApiIsLoaded, Pin } from '@vis.gl/react-google-maps';
import { SingleStoreType } from '@workspace/interfaces';
import { useInstantSearchLoadingStore } from '@workspace/widgets/AlgoliaSearch';
import { InfoWindowContent } from './MarkerComponents/InfoWindowContent';
import { MarkerRenderer } from './CustomMarker';
import { useGeoRadius } from '@workspace/widgets/AlgoliaSearch/GeoRadiusContext';
import { DEFAULT_VIEWPORT_PADDING } from '@workspace/widgets/MapView/hooks';

// Move supercluster options outside component to prevent recreation
const SUPERCLUSTER_OPTIONS = {
  radius: 80,
  maxZoom: 13,
} as const;

// Constant styles to prevent object recreation
const MARKER_STYLE = {
  cursor: 'default' as const,
};

// Constants for zoom levels
const MIN_ZOOM_FOR_SELECTED_STORE = 17;
const IDLE_DEBOUNCE_TIMEOUT = 500;

interface Props {
  selectedStore?: SingleStoreType | null;
  onSelectStore: (store: SingleStoreType) => void;
  showCenterMarker?: boolean;
  directionLabel: string | null | undefined;
}

export function MapView({
  selectedStore,
  onSelectStore,
  showCenterMarker = false,
  directionLabel,
}: Omit<Props, 'className' | 'mapZoom' | 'enableAutoLocate'>) {
  const programmaticChangeTracker = useRef({
    isActive: false,
    pendingOperations: new Set<string>(),
  });
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const apiIsLoaded = useApiIsLoaded();
  const map = useMap();

  const isReady = useMemo(() => {
    return apiIsLoaded && map;
  }, [apiIsLoaded, map]);

  const { hasUserInteracted, setHasUserInteracted, center, setConfigureProps, configureProps } = useGeoRadius();

  const { isLoading, results } = useInstantSearchLoadingStore();

  const itemsLength = useMemo(() => {
    return results.hits.length ?? 0;
  }, [results.hits.length]);

  // Helper functions for programmatic change tracking
  const startProgrammaticChange = useCallback((operationId: string) => {
    programmaticChangeTracker.current.pendingOperations.add(operationId);
    programmaticChangeTracker.current.isActive = true;
  }, []);

  const endProgrammaticChange = useCallback((operationId?: string) => {
    if (operationId) {
      programmaticChangeTracker.current.pendingOperations.delete(operationId);
    } else {
      programmaticChangeTracker.current.pendingOperations.clear();
    }
    programmaticChangeTracker.current.isActive = programmaticChangeTracker.current.pendingOperations.size > 0;
  }, []);

  // Radius calculation and auto-expansion logic
  useEffect(() => {
    if (!isReady || !map || isLoading || hasUserInteracted || !!configureProps.insideBoundingBox) {
      return;
    }

    // Only expand from 15km to 30km if we have < 3 stores
    if (itemsLength < 3) {
      startProgrammaticChange('radius-expansion');
      setConfigureProps((prevState: ConfigureProps) => ({
        ...prevState,
        aroundRadius: ESearchRadiusLevels.LEVEL_2,
      }));
      const boundingBox = calculateBoundsForRadius(center, ESearchRadiusLevels.LEVEL_2);
      map.fitBounds(boundingBox);
    }
  }, [
    isReady,
    center,
    hasUserInteracted,
    isLoading,
    itemsLength,
    map,
    setConfigureProps,
    configureProps.insideBoundingBox,
    startProgrammaticChange,
  ]);

  // useEffect to track center changes from context
  useEffect(() => {
    if (!isReady || !map || hasUserInteracted) return;

    startProgrammaticChange('center-change');
    const position = new google.maps.LatLng(center.lat, center.lng);
    map.panTo(position);
    const boundingBox = calculateBoundsForRadius(center, ESearchRadiusLevels.LEVEL_1);
    map.fitBounds(boundingBox);
  }, [isReady, center, hasUserInteracted, map, startProgrammaticChange]);

  // Setup map event listeners for idle (debounced bounds search)
  useEffect(() => {
    if (!isReady || !map) return;

    // Debounced idle listener for bounds-based search
    const idleListener = map.addListener('idle', () => {
      // Reset programmatic change tracker when map becomes idle
      endProgrammaticChange();

      if (!hasUserInteracted) return;

      // Clear any pending timeout
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      // Debounce for search
      idleTimeoutRef.current = setTimeout(() => {
        const bounds = map.getBounds();
        const zoom = map.getZoom();
        if (bounds) {
          const boundingBox = calculateInsideBoundingBox(bounds, DEFAULT_VIEWPORT_PADDING, zoom || 0);
          setConfigureProps((prevState: ConfigureProps) => ({
            ...prevState,
            insideBoundingBox: boundingBox,
          }));
        }
        idleTimeoutRef.current = null;
      }, IDLE_DEBOUNCE_TIMEOUT);
    });

    // Track user interaction (any bounds change not programmatic)
    const userInteractionListener = map.addListener('bounds_changed', () => {
      // Only set interaction flag if not a programmatic change
      if (!programmaticChangeTracker.current.isActive) {
        setHasUserInteracted(true);
      }
    });

    return () => {
      idleListener.remove();
      userInteractionListener.remove();
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    };
  }, [isReady, map, setConfigureProps, setHasUserInteracted, hasUserInteracted, endProgrammaticChange]);

  const handleMarkerClick = useCallback(
    (store: SingleStoreType) => {
      onSelectStore(store);
    },
    [onSelectStore],
  );

  // Memoize items to prevent unnecessary MarkerRenderer re-renders
  const memoizedItems = useMemo(() => {
    return results?.hits || [];
  }, [results.hits]);

  // Pan/zoom map to selected store
  useEffect(() => {
    if (!isReady || !selectedStore || !map) return;

    const lat = selectedStore._geoloc?.lat ?? null;
    const lng = selectedStore._geoloc?.lng ?? null;

    if (lat != null && lng != null) {
      startProgrammaticChange('selected-store');
      const position = new google.maps.LatLng(Number(lat), Number(lng));
      map.panTo(position);

      // Optionally adjust zoom level
      const currentZoom = map.getZoom();
      if (currentZoom && currentZoom < MIN_ZOOM_FOR_SELECTED_STORE) {
        map.setZoom(MIN_ZOOM_FOR_SELECTED_STORE);
      }
    }
  }, [isReady, map, selectedStore, startProgrammaticChange]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {/* <VisualCircle />
      <VisualBoundingBox
        boundingBox={
          reverseBoxToBoundsData(configureProps.insideBoundingBox)[0]
        }
      /> */}
      {showCenterMarker && (
        <AdvancedMarker position={SYD_TOWN_HALL} title="Sydney Town Hall" style={MARKER_STYLE}>
          <Pin background={'#CD1543'} borderColor={'white'} glyphColor={'white'} />
        </AdvancedMarker>
      )}
      <MarkerRenderer
        items={memoizedItems}
        handleMarkerClick={handleMarkerClick}
        superclusterOptions={SUPERCLUSTER_OPTIONS}
      />
      <InfoWindowContent store={selectedStore ?? null} onClose={onSelectStore} directionLabel={directionLabel} />
    </>
  );
}
